import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { servicesAPI, categoriesAPI, itemsAPI, settingsAPI, catalogsAPI, clientsAPI } from '@/lib/api';
import GlobalFilters from '@/components/GlobalFilters';
import { useGlobalFilters } from '@/hooks/useGlobalFilters';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { WhatsAppButton, WhatsAppQuickActions } from '@/components/WhatsAppButton';
import { TABLE_DENSITY } from '@/lib/tableDensity';
import { 
  Plus, Wrench, User, Phone, Mail, Laptop, Smartphone, Monitor, 
  Clock, Play, CheckCircle, AlertTriangle, Package, Store, 
  ClipboardCheck, Timer, Battery, Eye, Settings, Edit, History, AlertCircle, Lock,
  Receipt, Trash2, FileText, CreditCard, Gamepad2, Printer, HardHat, MapPin, Pen,
  ShieldCheck, ArrowRightLeft, ShoppingBag, MoreHorizontal
} from 'lucide-react';
import { formatDateTime, formatCurrency, formatDisplayOnlyCode, cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import DynamicSpecsForm from '@/components/DynamicSpecsForm';
import ServiceDetailModal from '@/components/services/ServiceDetailModal';
import { UniversalCrudActions } from '@/components/UniversalCrudActions';
import { ModulePageHero } from '@/components/common/ModulePageHero';

// P0-CORE: Import equipment source selectors
import { WarrantySaleSearch, RepairStockSelector, InternalAssetSelector } from '@/components/services/EquipmentSourceSelector';

// =============================================================================
// SERVICE MODULE TABS COMPONENT
// =============================================================================
const SERVICE_MODULES = {
  repairs: { key: 'repairs', label: 'Reparações', icon: Wrench, color: 'bg-orange-500 hover:bg-orange-600', prefix: 'SRV', serviceType: 'repairs' },
  garantias: { key: 'garantias', label: 'Garantias', icon: ShieldCheck, color: 'bg-green-500 hover:bg-green-600', prefix: 'WAR', serviceType: 'warranty_claim' },
  gaming: { key: 'gaming', label: 'Gaming Center', icon: Gamepad2, color: 'bg-purple-500 hover:bg-purple-600', prefix: 'GAM', serviceType: 'gaming' },
  papelaria: { key: 'papelaria', label: 'Papelaria', icon: Printer, color: 'bg-blue-500 hover:bg-blue-600', prefix: 'PAP', serviceType: 'papelaria' },
  outros: { key: 'outros', label: 'Outros', icon: Package, color: 'bg-slate-500 hover:bg-slate-600', prefix: 'OTH', serviceType: 'outros' },
  obras: { key: 'obras', label: 'Obras/Projectos', icon: HardHat, color: 'bg-amber-500 hover:bg-amber-600', prefix: 'PRJ', serviceType: 'obras' }
};

function ServiceModuleTabs({ activeModule, setActiveModule }) {
  const navigate = useNavigate();
  
  const handleModuleChange = (module) => {
    setActiveModule(module);
    navigate(`/services?module=${module}`, { replace: true });
  };
  
  return (
    <div className="flex flex-wrap gap-1.5 border-b border-slate-200 bg-white px-3 py-2.5 shadow-sm">
      {Object.entries(SERVICE_MODULES).map(([key, config]) => {
        const Icon = config.icon;
        const isActive = activeModule === key;
        return (
          <Button
            key={key}
            variant={isActive ? 'default' : 'ghost'}
            size="sm"
            onClick={() => handleModuleChange(key)}
            className={cn(
              "h-8 max-w-full gap-1.5 rounded-full px-3 text-[11px] transition-colors whitespace-normal text-left",
              isActive ? config.color + " text-white" : "text-slate-600 hover:text-slate-900"
            )}
            data-testid={`module-${key}`}
          >
            <Icon className="h-4 w-4" />
            {config.label}
          </Button>
        );
      })}
    </div>
  );
}

// Export for use in other modules
export { ServiceModuleTabs, SERVICE_MODULES };

// Import specialized pages for conditional rendering
import PapelariaPage from './PapelariaPage';
import GamingCenterPage from './GamingCenterPage';
import ObrasProjectosPage from './ObrasProjectosPage';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  reception: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  diagnosis: 'bg-purple-100 text-purple-800 border-purple-300',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
  awaiting_parts: 'bg-orange-100 text-orange-800 border-orange-300',
  awaiting_approval: 'bg-amber-100 text-amber-800 border-amber-300',
  ready_for_pickup: 'bg-green-100 text-green-800 border-green-300',
  completed: 'bg-slate-100 text-slate-800 border-slate-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300'
};

const statusLabels = {
  pending: 'Pendente',
  reception: 'Em Recepção',
  diagnosis: 'Em Diagnóstico',
  in_progress: 'Em Progresso',
  awaiting_parts: 'Aguarda Peças',
  awaiting_approval: 'Aguarda Aprovação',
  ready_for_pickup: 'Pronto p/ Levantar',
  completed: 'Concluído',
  cancelled: 'Cancelado'
};

const timeStatusColors = {
  not_started: 'text-slate-500',
  on_time: 'text-green-600',
  warning: 'text-amber-600',
  overdue: 'text-red-600'
};

const DIAGNOSTIC_COSTS = {
  simple: 1000,
  complex: 1800,
  no_charge: 0
};

const CATEGORY_ICONS = {
  laptop: Laptop,
  smartphone: Smartphone,
  desktop: Monitor,
};

// Field labels for audit display
const FIELD_LABELS = {
  customer_name: 'Nome do Cliente',
  customer_phone: 'Telefone',
  customer_email: 'Email',
  device_category: 'Categoria',
  device_brand: 'Marca',
  device_model: 'Modelo',
  device_serial: 'IMEI/SN',
  diagnostic_type: 'Tipo de Diagnóstico',
  estimated_hours: 'Tempo Estimado',
  notes: 'Notas',
  status: 'Estado',
  diagnosis: 'Diagnóstico',
  repair_notes: 'Notas de Reparação'
};

const getAuditChangeKey = (change, index) => {
  const stableParts = [
    change?.field,
    change?.old_value,
    change?.new_value,
  ].filter((value) => value !== undefined && value !== null && value !== '');

  return stableParts.length > 0 ? `audit-change-${stableParts.join('-')}` : `audit-change-${index}`;
};

// Status options for filter
const SERVICES_STATUS_OPTIONS = ['pending', 'reception', 'diagnosis', 'in_progress', 'awaiting_parts', 'awaiting_approval', 'ready_for_pickup', 'completed', 'cancelled'];
const INVENTORY_TABLE_HEAD_CLASS = TABLE_DENSITY.head;
const INVENTORY_TABLE_CELL_CLASS = TABLE_DENSITY.cell;
const INVENTORY_TABLE_ROW_CLASS = TABLE_DENSITY.row;

export default function ServicesPage() {
  const navigate = useNavigate();
  // URL Search Params for filter persistence
  const [searchParams, setSearchParams] = useSearchParams();
  const serviceNumberFilter = searchParams.get('service_number') || '';
  const qaDocFilter = searchParams.get('qa_doc') || '';
  const projectIdFilter = searchParams.get('project_id') || '';
  const serviceNumberFilterLabel = serviceNumberFilter
    ? serviceNumberFilter.split(',').map((code) => formatDisplayOnlyCode(code.trim(), code.trim().startsWith('WAR-') ? 'WAR' : 'SRV')).join(', ')
    : '';
  
  // Service module selector (repairs, gaming, papelaria, outros)
  const [activeModule, setActiveModule] = useState(searchParams.get('module') || 'repairs');
  
  const { user, hasRole, defaultDocumentLocationId } = useAuth();

  // ALL hooks must be declared before any conditional returns
  // Global filters
  const { filters, setFilters } = useGlobalFilters({
    period: searchParams.get('period') || 'all',
    location_id: searchParams.get('location_id') || null,
    employee_id: searchParams.get('employee_id') || null,
    status: searchParams.get('status') || null,
  });
  
  const [services, setServices] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productCategories, setProductCategories] = useState([]); // Full catalog - 15 categories
  const [schemas, setSchemas] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [receptionDialogOpen, setReceptionDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [confirmEditDialogOpen, setConfirmEditDialogOpen] = useState(false);
  const [adminNoteDialogOpen, setAdminNoteDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilter, setAuditFilter] = useState({ user: '', date: '' });
  const [editFormData, setEditFormData] = useState(null);
  const [editJustification, setEditJustification] = useState('');
  const [checklistCorrectionReason, setChecklistCorrectionReason] = useState('');
  const [adminNoteText, setAdminNoteText] = useState('');
  const [adminNoteJustification, setAdminNoteJustification] = useState('');
  const [storeItems, setStoreItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [quickClientOpen, setQuickClientOpen] = useState(false);
  const [creatingQuickClient, setCreatingQuickClient] = useState(false);
  const skipNextServicesReloadRef = useRef(false);
  const [quickClientForm, setQuickClientForm] = useState({
    name: '',
    phone: '',
    nuit: '',
    address: '',
    city: ''
  });
  
  // Repair Workflow Modal
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [workflowServiceId, setWorkflowServiceId] = useState(null);
  
  // Invoice & Delete dialogs
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  
  // Permission checks
  const isAdmin = hasRole(['admin']);
  const isManager = hasRole(['store_manager']);
  const canEdit = hasRole(['admin', 'store_manager']); // Only Admin and Manager can edit
  const canEditDiagnosticCost = isAdmin; // Only Admin can edit diagnostic cost
  const canDelete = isAdmin || isManager; // Only Admin and Manager can delete
  const canInvoice = hasRole(['admin', 'store_manager', 'cashier']); // Can generate invoice
  const activeServiceLocationId = filters.location_id || 'all';
  const activeServiceLocation = locations.find((location) => location.id === activeServiceLocationId) || null;
  const defaultDocumentLocation = locations.find((location) => location.id === defaultDocumentLocationId) || null;
  const storeLocationOptions = locations.filter((location) => location.type === 'store');
  
  // Status that allows editing
  const EDITABLE_STATUSES = ['pending', 'reception', 'diagnosis', 'in_progress', 'awaiting_parts', 'awaiting_approval'];
  const CLOSED_STATUSES = ['ready_for_pickup', 'completed', 'cancelled'];
  
  const canEditService = (service) => {
    return canEdit && EDITABLE_STATUSES.includes(service.status);
  };
  
  const canAddAdminNote = (service) => {
    return isAdmin && CLOSED_STATUSES.includes(service.status);
  };

  const normalizeAuditChanges = (changes) => {
    if (Array.isArray(changes)) return changes;
    if (!changes || typeof changes !== 'object') return [];

    return Object.entries(changes).map(([field, value]) => {
      if (value && typeof value === 'object' && ('old_value' in value || 'new_value' in value)) {
        return {
          field,
          old_value: value.old_value,
          new_value: value.new_value,
        };
      }

      return {
        field,
        old_value: '-',
        new_value: typeof value === 'object' ? JSON.stringify(value) : String(value),
      };
    });
  };

  const calculateServiceGrossTotal = (service) => {
    const quotation = service?.quotation || {};
    const diagnosticCost = Number(quotation.diagnostic_cost ?? service?.diagnostic_cost ?? 0);
    const labourCost = Number(quotation.labour_cost ?? service?.labour_cost ?? service?.labor_cost ?? 0);
    const partsTotal = Number(quotation.parts_total ?? service?.parts_total ?? 0);
    const discount = Number(quotation.discount ?? service?.discount ?? 0);
    const taxableBase = Math.max(0, diagnosticCost + labourCost + partsTotal - discount);
    const vatEnabled = Boolean(quotation.vat_enabled || quotation.apply_vat);
    const vatRate = Number(quotation.vat_rate ?? 16);
    const vatAmount = vatEnabled ? (taxableBase * vatRate) / 100 : 0;
    return taxableBase + vatAmount;
  };
  
  const initialFormData = {
    client_id: '',
    use_default_client: false,
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    device_category: '',
    device_brand: '',
    device_model: '',
    device_serial: '',
    device_specs: {},
    article_origin: 'client',
    service_case_type: 'paid_repair',
    store_item_id: '',
    // Warranty claim fields
    sale_id: '',
    sale_item_id: '',
    sale_item_index: 0,
    location_id: searchParams.get('location_id') || null,
    purchase_date: '',
    warranty_months: '',
    original_invoice_no: '',
    issue_description: '',
    diagnostic_type: 'simple',
    skip_diagnostic_charge: false,
    estimated_hours: '',
    notes: '',
    registered_at: ''  // Custom date/time
  };
  
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    setSelectedServiceIds([]);
  }, [services, activeModule]);
  
  const [receptionChecklist, setReceptionChecklist] = useState({
    physical_condition_verified: false,
    accessories_registered: false,
    customer_complaint_recorded: false,
    diagnosis_authorization: false,
    customer_data_confirmed: false,
    physical_condition_notes: '',
    accessories_list: ''
  });

  const currentSchema = formData.device_category ? schemas[formData.device_category] : null;

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeModule && activeModule !== 'repairs') params.set('module', activeModule);
    if (filters.period && filters.period !== 'all') params.set('period', filters.period);
    if (filters.location_id) params.set('location_id', filters.location_id);
    if (filters.employee_id) params.set('employee_id', filters.employee_id);
    if (filters.status) params.set('status', filters.status);
    if (serviceNumberFilter) params.set('service_number', serviceNumberFilter);
    if (qaDocFilter) params.set('qa_doc', qaDocFilter);
    if (activeModule === 'obras' && projectIdFilter) params.set('project_id', projectIdFilter);
    setSearchParams(params, { replace: true });
  }, [filters, activeModule, setSearchParams, serviceNumberFilter, qaDocFilter, projectIdFilter]);

  const loadServices = useCallback(async () => {
    try {
      // Build params from filters
      const params = {
        period: filters.period || 'all',
      };
      
      // Filter by service module prefix
      const moduleConfig = SERVICE_MODULES[activeModule];
      if (moduleConfig?.prefix) {
        // Support array of prefixes for backwards compatibility
        const prefixes = Array.isArray(moduleConfig.prefix) ? moduleConfig.prefix : [moduleConfig.prefix];
        params.service_code_prefix = prefixes.join(',');
      }
      if (moduleConfig?.serviceType) {
        params.service_type = moduleConfig.serviceType;
      }

      if (activeModule === 'garantias') {
        params.service_type = 'warranty_claim';
      }
      
      if (filters.location_id) params.location_id = filters.location_id;
      if (filters.employee_id) params.employee_id = filters.employee_id;
      if (filters.status) params.status_filter = filters.status;
      if (filters.custom_start) params.start_date = filters.custom_start;
      if (filters.custom_end) params.end_date = filters.custom_end;
      if (serviceNumberFilter) params.service_number = serviceNumberFilter;
      
      const response = await servicesAPI.list(params);
      setServices(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar serviços');
    }
  }, [filters, activeModule, serviceNumberFilter]);

  const loadLookupData = useCallback(async () => {
    const [categoriesRes, schemasRes, productCategoriesRes, clientsRes, locationsRes] = await Promise.allSettled([
      categoriesAPI.list(),
      categoriesAPI.getAllSchemas(),
      catalogsAPI.listProductCategories(),
      clientsAPI.listActive({ compact: true }),
      settingsAPI.listActiveLocations(),
    ]);

    if (categoriesRes.status === 'fulfilled') setCategories(categoriesRes.value.data || []);
    if (schemasRes.status === 'fulfilled') setSchemas(schemasRes.value.data || {});
    if (productCategoriesRes.status === 'fulfilled') setProductCategories(productCategoriesRes.value.data || []);
    if (clientsRes.status === 'fulfilled') setClients(clientsRes.value.data || []);
    if (locationsRes.status === 'fulfilled') setLocations(locationsRes.value.data || []);
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      skipNextServicesReloadRef.current = true;
      await loadServices();
    } catch {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
      void loadLookupData();
    }
  }, [loadLookupData, loadServices]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (!loading) {
      if (skipNextServicesReloadRef.current) {
        skipNextServicesReloadRef.current = false;
        return;
      }
      loadServices();
    }
  }, [loading, loadServices]);

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, [setFilters]);

  const handleServiceLocationChange = useCallback((value) => {
    setFilters({ location_id: value === 'all' ? null : value });
  }, [setFilters]);

  // Handle module change
  const handleModuleChange = useCallback((module) => {
    setActiveModule(module);
  }, []);

  // Refresh function for GlobalFilters
  const handleRefresh = useCallback(() => {
    loadServices();
  }, [loadServices]);

  // Load store items when article origin changes to 'store'
  const loadStoreItems = useCallback(async (categoryKey) => {
    if (!categoryKey) return;
    setLoadingItems(true);
    try {
      const response = await itemsAPI.list({ state: 'in_stock' });
      // Filter by category if needed (items don't have category directly, need to check product)
      setStoreItems(response.data || []);
    } catch (error) {
      console.error('Failed to load items:', error);
    } finally {
      setLoadingItems(false);
    }
  }, []);

  const handleCategoryChange = useCallback((categoryKey) => {
    setFormData((prev) => ({
      ...prev,
      device_category: categoryKey,
      device_specs: {}
    }));
    
    if (formData.article_origin === 'store') {
      loadStoreItems(categoryKey);
    }
  }, [formData.article_origin, loadStoreItems]);

  const handleArticleOriginChange = useCallback((origin) => {
    setFormData((prev) => ({
      ...prev,
      article_origin: origin,
      store_item_id: ''
    }));
    
    if (origin === 'store' && formData.device_category) {
      loadStoreItems(formData.device_category);
    }
  }, [formData.device_category, loadStoreItems]);

  const filteredClients = clients.filter((client) => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      (client.name || '').toLowerCase().includes(q) ||
      (client.phone || '').toLowerCase().includes(q) ||
      (client.code || client.client_code || '').toLowerCase().includes(q)
    );
  });

  const applySelectedClient = (client) => {
    if (!client) return;
    setFormData((prev) => ({
      ...prev,
      client_id: client.id,
      use_default_client: (client.code || client.client_code) === 'CL-000000',
      customer_name: client.name || '',
      customer_phone: client.phone || ((client.code || client.client_code) === 'CL-000000' ? 'N/A' : ''),
      customer_email: client.email || ''
    }));
  };

  const handleUseDefaultClient = async () => {
    try {
      const response = await clientsAPI.getDefault();
      const defaultClient = response.data;
      setClients((prev) => {
        if (prev.some((c) => c.id === defaultClient.id)) return prev;
        return [defaultClient, ...prev];
      });
      applySelectedClient(defaultClient);
      toast.success('Cliente Indiferenciado autorizado para este serviço');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar Cliente Indiferenciado');
    }
  };

  const handleQuickCreateClient = async () => {
    if (!quickClientForm.name.trim() || !quickClientForm.phone.trim()) {
      toast.error('Nome e telefone são obrigatórios no cadastro rápido');
      return;
    }

    setCreatingQuickClient(true);
    try {
      const payload = {
        name: quickClientForm.name.trim(),
        phone: quickClientForm.phone.trim(),
        nuit: quickClientForm.nuit.trim() || null,
        address: quickClientForm.address.trim() || null,
        city: quickClientForm.city.trim() || null,
      };
      const response = await clientsAPI.create(payload);
      const createdClient = response.data;
      setClients((prev) => [createdClient, ...prev]);
      applySelectedClient(createdClient);
      setQuickClientOpen(false);
      setQuickClientForm({ name: '', phone: '', nuit: '', address: '', city: '' });
      toast.success('Cliente criado e selecionado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar cliente');
    } finally {
      setCreatingQuickClient(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.article_origin !== 'warranty_claim' && !formData.client_id) {
      toast.error('Cliente obrigatório');
      return;
    }

    if (formData.article_origin === 'warranty_claim' && (!formData.sale_id || !formData.sale_item_id)) {
      toast.error('Selecione venda e item para abrir reclamação de garantia');
      return;
    }
    
    try {
      if (formData.article_origin === 'warranty_claim') {
        const payload = {
          sale_id: formData.sale_id,
          sale_item_id: formData.sale_item_id,
          sale_item_index: Number(formData.sale_item_index || 0),
          issue_description: formData.issue_description,
          customer_phone: formData.customer_phone || null,
          location_id: filters.location_id || formData.location_id || null,
          priority: formData.priority || 'normal'
        };

        const response = await servicesAPI.createWarrantyClaim(payload);
        const createdServiceId = response?.data?.service_id;

        if (createdServiceId) {
          try {
            const createdService = await servicesAPI.get(createdServiceId);
            if (createdService?.data) {
              setServices((prev) => [createdService.data, ...prev]);
            }
          } catch (error) {
            console.error('Error fetching created warranty service:', error);
          }
        }

        toast.success(`Garantia criada: ${formatDisplayOnlyCode(response?.data?.service_number || '', 'WAR')}`);
        setDialogOpen(false);
        setFormData(initialFormData);
        setClientSearch('');
        setQuickClientOpen(false);
        setQuickClientForm({ name: '', phone: '', nuit: '', address: '', city: '' });
        await loadServices();
        return;
      }

      const serviceData = {
        ...formData,
        service_type: activeModule, // repairs ou outros
        location_id: filters.location_id || formData.location_id || null,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        store_item_id: formData.article_origin === 'store' ? formData.store_item_id : null,
        registered_at: formData.registered_at ? new Date(formData.registered_at).toISOString() : null,
        // Warranty fields
        warranty_months: formData.warranty_months ? parseInt(formData.warranty_months) : null,
        purchase_date: formData.purchase_date ? new Date(formData.purchase_date).toISOString() : null,
        // Skip diagnostic for internal + warranty/no-charge
        skip_diagnostic_charge:
          formData.article_origin === 'store_equipment' ||
          formData.service_case_type === 'store_internal' ||
          formData.article_origin === 'warranty_claim' ||
          formData.service_case_type === 'warranty_claim' ||
          formData.diagnostic_type === 'no_charge',
        // Don't send diagnostic_type if blocked
        diagnostic_type: (
          formData.article_origin === 'store_equipment' ||
          formData.service_case_type === 'store_internal' ||
          formData.article_origin === 'warranty_claim' ||
          formData.service_case_type === 'warranty_claim'
        )
          ? 'no_charge'
          : formData.diagnostic_type
      };
      
      const response = await servicesAPI.create(serviceData);
      toast.success('Serviço criado com sucesso!');
      setDialogOpen(false);
      setFormData(initialFormData);
      setClientSearch('');
      setQuickClientOpen(false);
      setQuickClientForm({ name: '', phone: '', nuit: '', address: '', city: '' });

      // Immediate list update (without waiting manual refresh)
      if (response?.data) {
        setServices((prev) => [response.data, ...prev]);
      }
      await loadServices();
    } catch (error) {
      const duplicateDetail = error?.response?.data?.detail;
      if (duplicateDetail?.code === 'warranty_duplicate') {
        toast.error(`${duplicateDetail.message} Serviço: ${formatDisplayOnlyCode(duplicateDetail.existing_service_number || '', 'SRV')}`.trim());
        return;
      }
      toast.error(error.response?.data?.detail || 'Erro ao criar serviço');
    }
  };

  const handleViewDetails = (service) => {
    // Open the advanced workflow modal for repair services
    setWorkflowServiceId(service.id);
    setWorkflowModalOpen(true);
    setSelectedService(service);
  };

  // Edit functionality
  const handleOpenEdit = (service) => {
    // Check if service is in closed status
    if (CLOSED_STATUSES.includes(service.status)) {
      toast.error('Serviço fechado. Use "Nota Administrativa" para adicionar observações.');
      return;
    }
    
    // Check permissions
    if (!canEdit) {
      toast.error('Sem permissão para editar serviços');
      return;
    }
    
    // Check if service has started (for field locking)
    const serviceStarted = !!service.time_tracking?.started_at;
    
    setSelectedService(service);
    setEditFormData({
      customer_name: service.customer_name,
      customer_phone: service.customer_phone,
      customer_email: service.customer_email || '',
      device_category: service.device_category,
      device_brand: service.device_brand || '',
      device_model: service.device_model || '',
      device_serial: service.device_serial || '',
      diagnostic_type: service.diagnostic_type,
      estimated_hours: service.time_tracking?.estimated_hours?.toString() || '',
      notes: service.notes || '',
      // Track if fields should be locked
      _serviceStarted: serviceStarted
    });
    setEditJustification('');
    setEditDialogOpen(true);
  };

  // Admin Note functionality
  const handleOpenAdminNote = (service) => {
    if (!isAdmin) {
      toast.error('Apenas Admin pode adicionar notas administrativas');
      return;
    }
    setSelectedService(service);
    setAdminNoteText('');
    setAdminNoteJustification('');
    setAdminNoteDialogOpen(true);
  };

  const handleSaveAdminNote = async () => {
    if (!selectedService?.id) {
      toast.error('Serviço inválido para nota administrativa');
      return;
    }

    if (!adminNoteText || adminNoteText.trim().length < 5) {
      toast.error('Nota obrigatória (mínimo 5 caracteres)');
      return;
    }
    if (!adminNoteJustification || adminNoteJustification.trim().length < 5) {
      toast.error('Justificação obrigatória (mínimo 5 caracteres)');
      return;
    }
    
    try {
      await servicesAPI.addAdminNote(selectedService.id, adminNoteText.trim(), adminNoteJustification.trim());
      toast.success('Nota administrativa adicionada!');
      setAdminNoteDialogOpen(false);
      setAdminNoteText('');
      setAdminNoteJustification('');
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar nota');
    }
  };

  const handleSaveEdit = () => {
    // Validate justification
    if (!editJustification || editJustification.trim().length < 5) {
      toast.error('Justificação obrigatória (mínimo 5 caracteres)');
      return;
    }
    // Show confirmation dialog
    setConfirmEditDialogOpen(true);
  };

  const handleConfirmEdit = async () => {
    if (!selectedService?.id || !editFormData) {
      toast.error('Serviço inválido para edição');
      return;
    }

    try {
      // Use the new editWithAudit API
      await servicesAPI.editWithAudit(selectedService.id, {
        ...editFormData,
        skip_diagnostic_charge: editFormData.diagnostic_type === 'no_charge' || editFormData.skip_diagnostic_charge,
        estimated_hours: editFormData.estimated_hours ? parseFloat(editFormData.estimated_hours) : null
      }, editJustification.trim());
      
      toast.success('Serviço actualizado! Alteração registada no histórico.');
      setConfirmEditDialogOpen(false);
      setEditDialogOpen(false);
      setEditJustification('');
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao actualizar serviço');
    }
  };

  // Audit log functionality
  const handleViewAuditLog = async (service) => {
    setSelectedService(service);
    try {
      const response = await servicesAPI.getAuditLog(service.id);
      setAuditLogs(response.data);
      setAuditDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const handleOpenReception = (service) => {
    setSelectedService(service);
    // Pre-fill with existing data if reopening
    if (service.reception_checklist) {
      setReceptionChecklist({
        ...service.reception_checklist,
        accessories_list: Array.isArray(service.reception_checklist.accessories_list) 
          ? service.reception_checklist.accessories_list.join(', ')
          : service.reception_checklist.accessories_list || ''
      });
    } else {
      setReceptionChecklist({
        physical_condition_verified: false,
        accessories_registered: false,
        customer_complaint_recorded: false,
        diagnosis_authorization: false,
        customer_data_confirmed: false,
        physical_condition_notes: '',
        accessories_list: ''
      });
    }
    setChecklistCorrectionReason('');
    setReceptionDialogOpen(true);
  };

  const handleCompleteReception = async () => {
    if (!selectedService?.id) {
      toast.error('Serviço inválido para recepção');
      return;
    }

    const allChecked = 
      receptionChecklist.physical_condition_verified &&
      receptionChecklist.accessories_registered &&
      receptionChecklist.customer_complaint_recorded &&
      receptionChecklist.diagnosis_authorization &&
      receptionChecklist.customer_data_confirmed;
    
    if (!allChecked) {
      toast.error('Complete todos os itens da checklist');
      return;
    }
    
    // Require correction reason if editing existing checklist
    if (selectedService.reception_checklist && (!checklistCorrectionReason || checklistCorrectionReason.trim().length < 5)) {
      toast.error('Motivo da correcção obrigatório (mínimo 5 caracteres)');
      return;
    }
    
    try {
      const checklistData = {
        ...receptionChecklist,
        accessories_list: receptionChecklist.accessories_list 
          ? receptionChecklist.accessories_list.split(',').map(s => s.trim())
          : []
      };
      
      // Use update if already has checklist, otherwise complete
      if (selectedService.reception_checklist) {
        await servicesAPI.updateReception(selectedService.id, checklistData, checklistCorrectionReason.trim());
        toast.success('Checklist actualizada! Alteração registada no histórico.');
      } else {
        await servicesAPI.completeReception(selectedService.id, checklistData);
        toast.success('Recepção concluída!');
      }
      setReceptionDialogOpen(false);
      setChecklistCorrectionReason('');
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao completar recepção');
    }
  };

  const handleStartService = async (service) => {
    try {
      await servicesAPI.start(service.id);
      toast.success('Serviço iniciado!');
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar serviço');
    }
  };

  const handleCompleteService = async (service) => {
    try {
      await servicesAPI.complete(service.id);
      toast.success('Serviço concluído!');
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao concluir serviço');
    }
  };

  // Generate invoice/receipt for service
  const handleGenerateInvoice = async () => {
    if (!selectedService) return;
    
    try {
      const invoiceLocationId = defaultDocumentLocationId || filters.location_id || selectedService.location_id || null;
      const response = await servicesAPI.completeAndInvoice(selectedService.id, invoiceLocationId);
      const finalCost = response?.data?.total ?? calculateServiceGrossTotal(selectedService);
      
      toast.success(`Factura gerada! Total: ${formatCurrency(finalCost)}`, {
        description: `Serviço: ${formatDisplayOnlyCode(selectedService.service_number, 'SRV')} | Factura: ${formatDisplayOnlyCode(response?.data?.invoice_number || '', 'INV')}`
      });
      setInvoiceDialogOpen(false);
      await loadServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar factura');
    }
  };

  // Delete service (admin/manager only)
  const handleDeleteService = async () => {
    if (!selectedService || !deleteReason.trim()) {
      toast.error('Motivo de eliminação é obrigatório');
      return;
    }
    
    try {
      await servicesAPI.delete(selectedService.id, deleteReason);
      toast.success('Serviço eliminado com sucesso!');
      setDeleteDialogOpen(false);
      setDeleteReason('');
      setSelectedService(null);
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar serviço');
    }
  };

  const toggleServiceSelection = (serviceId, checked) => {
    setSelectedServiceIds((prev) => {
      if (checked) {
        return prev.includes(serviceId) ? prev : [...prev, serviceId];
      }
      return prev.filter((id) => id !== serviceId);
    });
  };

  const allServicesSelected = services.length > 0 && services.every((service) => selectedServiceIds.includes(service.id));

  const toggleSelectAllServices = (checked) => {
    if (checked) {
      setSelectedServiceIds(services.map((service) => service.id));
      return;
    }
    setSelectedServiceIds([]);
  };

  const handleBulkServiceStatus = async (action) => {
    if (!selectedServiceIds.length) {
      toast.error('Seleccione serviços para ação em massa');
      return;
    }
    try {
      const response = await servicesAPI.bulkStatus({
        ids: selectedServiceIds,
        action,
        reason: action === 'deactivate' ? 'Desativação em massa via lista' : 'Restauro em massa via lista',
      });
      toast.success(response.data.message || 'Ação em massa concluída');
      setSelectedServiceIds([]);
      await loadServices();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro na ação em massa'));
    }
  };

  const handleExportSelectedServices = () => {
    if (!selectedServiceIds.length) {
      toast.error('Seleccione serviços para exportar');
      return;
    }
    const selected = services.filter((service) => selectedServiceIds.includes(service.id));
    if (!selected.length) {
      toast.error('Nenhum serviço seleccionado para exportação');
      return;
    }

    const headers = ['Nº Serviço', 'Data/Hora', 'Cliente', 'Equipamento', 'Origem', 'Estado', 'Custo'];
    const rows = selected.map((service) => [
      formatDisplayOnlyCode(service.service_number, service.service_type === 'warranty_claim' ? 'WAR' : 'SRV'),
      formatDateTime(service.registered_at || service.created_at),
      service.customer_name || '',
      `${service.device_brand || ''} ${service.device_model || service.device_category || ''}`.trim(),
      service.article_origin || '',
      statusLabels[service.status] || service.status,
      calculateServiceGrossTotal(service),
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map((field) => `"${String(field).replace(/"/g, '""')}"`).join(';'))
      .join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `servicos_seleccionados_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
    toast.success(`${selected.length} serviços exportados`);
  };

  const handleDirectAdminDelete = async (service) => {
    try {
      await servicesAPI.delete(service.id, 'Eliminação directa pelo administrador');
      toast.success('Serviço eliminado pelo administrador');
      if (selectedService?.id === service.id) {
        setSelectedService(null);
      }
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar serviço');
    }
  };

  const getTimeStatusIcon = (timeTracking) => {
    if (!timeTracking) return null;
    const status = timeTracking.time_status;
    
    if (status === 'overdue') return <AlertTriangle className="h-4 w-4 text-red-500" />;
    if (status === 'warning') return <Clock className="h-4 w-4 text-amber-500" />;
    if (status === 'on_time') return <Timer className="h-4 w-4 text-green-500" />;
    return <Clock className="h-4 w-4 text-slate-400" />;
  };

  // Render specialized pages for gaming and papelaria
  if (activeModule === 'gaming') {
    return (
      <div className="flex flex-col h-full" data-testid="services-page">
        <ServiceModuleTabs activeModule={activeModule} setActiveModule={setActiveModule} />
        <GamingCenterPage />
      </div>
    );
  }
  
  if (activeModule === 'papelaria') {
    return (
      <div className="flex flex-col h-full" data-testid="services-page">
        <ServiceModuleTabs activeModule={activeModule} setActiveModule={setActiveModule} />
        <PapelariaPage />
      </div>
    );
  }

  if (activeModule === 'obras') {
    return (
      <div className="flex flex-col h-full" data-testid="services-page">
        <ServiceModuleTabs activeModule={activeModule} setActiveModule={setActiveModule} />
        <ObrasProjectosPage />
      </div>
    );
  }

  const activeModuleConfig = SERVICE_MODULES[activeModule] || SERVICE_MODULES.repairs;
  const activeModuleHeroCopy = {
    repairs: {
      title: 'Serviços e Reparações',
      description: '',
      badge: 'Assistência Técnica',
    },
    garantias: {
      title: 'Garantias Técnicas',
      description: '',
      badge: 'Fluxo de Garantia',
    },
    papelaria: {
      title: 'Papelaria & Impressão',
      description: 'Cockpit operacional para encomendas rápidas, produção gráfica, entregas e acompanhamento visual de filas.',
      badge: 'Papelaria',
    },
    gaming: {
      title: 'Gaming Center',
      description: '',
      badge: 'Experiência Gaming',
    },
    outros: {
      title: 'Outros Serviços',
      description: 'Apenas os serviços realmente classificados como “Outros”, sem misturar Garantias, Gaming ou Papelaria.',
      badge: 'Operações',
    },
  }[activeModule] || {
    title: activeModuleConfig.label,
    description: 'Painel operacional do módulo seleccionado.',
    badge: 'Serviços',
  };

  // Render repairs or outros services
  return (
    <div className="flex flex-col h-full" data-testid="services-page">
      {/* Service Module Quick-Access Tabs */}
      <ServiceModuleTabs activeModule={activeModule} setActiveModule={setActiveModule} />
      
      <div className="p-4 space-y-5">
        <ModulePageHero
          testId="services-page-hero"
          title={activeModuleHeroCopy.title}
          description={activeModuleHeroCopy.description}
          icon={activeModuleConfig.icon || Wrench}
          badge={activeModuleHeroCopy.badge}
          compact
          stats={[
            { label: 'Total', value: services.length, helper: 'Serviços listados no módulo actual' },
            { label: 'Aguarda Aprovação', value: services.filter((service) => service.status === 'awaiting_approval').length, helper: 'Cotações pendentes' },
            { label: 'Pronto p/ Levantar', value: services.filter((service) => service.status === 'ready_for_pickup').length, helper: 'Serviços finalizados' },
            { label: 'Concluídos', value: services.filter((service) => service.status === 'completed').length, helper: 'Histórico concluído' },
          ]}
          actions={(['repairs', 'outros', 'garantias', 'papelaria', 'gaming'].includes(activeModule)) ? (
            <Button
              onClick={() => {
                setFormData(initialFormData);
                setClientSearch('');
                setQuickClientOpen(false);
                setQuickClientForm({ name: '', phone: '', nuit: '', address: '', city: '' });
                setDialogOpen(true);
              }}
              data-testid="new-service-button"
              size="sm"
              className="h-10 rounded-xl px-4 text-sm"
            >
              <Plus className="mr-2 h-4 w-4" />
              Novo Serviço
            </Button>
          ) : null}
        />

        {serviceNumberFilter && (
          <Card className="border-primary/20 bg-primary/5" data-testid="services-qa-filter-banner">
            <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-medium">Filtro rápido QA activo</p>
                <p className="text-xs text-muted-foreground">
                  A lista mostra apenas: <span className="font-medium text-foreground">{serviceNumberFilterLabel}</span>
                  {qaDocFilter ? <> • Documento em foco: <span className="font-medium text-foreground">{qaDocFilter}</span></> : null}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSearchParams(activeModule === 'repairs' ? new URLSearchParams() : new URLSearchParams({ module: activeModule }))}
                data-testid="services-qa-clear-filter-btn"
              >
                Limpar filtro QA
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Global Filters */}
        <div className="rounded-[22px] border border-slate-200 bg-white p-3.5 shadow-sm" data-testid="services-ultracompact-filters-card">
          <div className="flex flex-wrap items-center gap-3" data-testid="services-context-controls-row">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 rounded-full px-4 text-sm"
              onClick={() => navigate('/procurement')}
              data-testid="services-open-procurement-button"
            >
              <ShoppingBag className="h-3.5 w-3.5 mr-1.5" />
              Compras
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 rounded-full px-4 text-sm"
              onClick={() => navigate('/stock-transfers')}
              data-testid="services-open-stock-transfers-button"
            >
              <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />
              Transferências
            </Button>
            <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700" data-testid="services-module-summary-badge-total">
              {services.length} registos
            </Badge>
            <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700" data-testid="services-module-summary-badge-ready">
              {services.filter((service) => service.status === 'ready_for_pickup').length} prontos
            </Badge>
            <GlobalFilters
              filters={['period', 'employee', 'status', 'deleted']}
              statusOptions={SERVICES_STATUS_OPTIONS}
              statusLabels={statusLabels}
              onChange={handleFilterChange}
              onRefresh={handleRefresh}
              loading={loading}
              defaultPeriod="all"
              compact
              className="min-w-0 flex-1 gap-2"
              filterTriggerClassName="rounded-full border-slate-200 bg-white text-[11px]"
              actionButtonClassName="rounded-full border-slate-200 bg-white"
              activeBadgeClassName="rounded-full text-[10px]"
            />
            <div className="flex min-w-0 flex-wrap items-center gap-2" data-testid="services-location-context-banner">
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold text-slate-700">
                <MapPin className="h-3.5 w-3.5 text-slate-500" /> Localização operacional
              </span>
              <Badge variant="outline" className="rounded-full text-[10px]" data-testid="services-active-location-badge">
                {activeServiceLocation ? `${activeServiceLocation.type === 'store' ? 'Loja' : 'Local'} · ${activeServiceLocation.name}` : 'Todas as localizações'}
              </Badge>
              {defaultDocumentLocation && (
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] text-slate-700" data-testid="services-default-document-location-copy">
                  Docs: <strong>{defaultDocumentLocation.name}</strong>
                </span>
              )}
              <div className="w-[220px] min-w-[220px]" data-testid="services-explicit-location-selector">
                <Select value={activeServiceLocationId} onValueChange={handleServiceLocationChange}>
                  <SelectTrigger className="h-8 rounded-full border-slate-200 bg-white text-[11px]" data-testid="services-location-selector">
                    <SelectValue placeholder="Todas as localizações" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as localizações</SelectItem>
                    {locations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.type === 'store' ? 'Loja' : location.type === 'warehouse' ? 'Armazém' : 'Local'} · {location.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap items-center gap-1.5" data-testid="services-location-quick-switches">
                <Button
                  type="button"
                  variant={activeServiceLocationId === 'all' ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 rounded-full px-3 text-[11px]"
                  onClick={() => handleServiceLocationChange('all')}
                  data-testid="services-location-pill-all"
                >
                  Todas
                </Button>
                {storeLocationOptions.map((location) => (
                  <Button
                    key={location.id}
                    type="button"
                    variant={activeServiceLocationId === location.id ? 'default' : 'outline'}
                    size="sm"
                    className="h-7 rounded-full px-3 text-[11px]"
                    onClick={() => handleServiceLocationChange(location.id)}
                    data-testid={`services-location-pill-${location.id}`}
                  >
                    {location.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

      {/* New Service Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="w-[min(96vw,1180px)] max-w-[1180px] max-h-[88vh] overflow-y-auto p-4 sm:p-5">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Novo Pedido de Serviço
            </DialogTitle>
            <DialogDescription>
              Selecione ou crie o cliente e preencha os dados do equipamento para registar o serviço.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <Tabs defaultValue="customer" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="customer">
                  <User className="h-4 w-4 mr-2" />
                  Cliente
                </TabsTrigger>
                <TabsTrigger value="device">
                  <Laptop className="h-4 w-4 mr-2" />
                  Equipamento
                </TabsTrigger>
                <TabsTrigger value="issue">
                  <Wrench className="h-4 w-4 mr-2" />
                  Problema
                </TabsTrigger>
                <TabsTrigger value="diagnostic">
                  <Settings className="h-4 w-4 mr-2" />
                  Diagnóstico
                </TabsTrigger>
              </TabsList>
              
              {/* Customer Tab */}
              <TabsContent value="customer" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 border rounded-lg bg-slate-50">
                  <div className="space-y-2">
                    <Label htmlFor="service-client-search">Pesquisar Cliente</Label>
                    <Input
                      id="service-client-search"
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      placeholder="Nome / Telefone / Código"
                      data-testid="service-client-search-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Selecionar Cliente *</Label>
                    <Select
                      value={formData.client_id || ''}
                      onValueChange={(value) => {
                        const client = clients.find((c) => c.id === value);
                        applySelectedClient(client);
                      }}
                    >
                      <SelectTrigger data-testid="service-client-select">
                        <SelectValue placeholder="Escolher cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredClients.slice(0, 300).map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {(client.code || client.client_code || 'CL-') + ' • ' + client.name + (client.phone ? ` (${client.phone})` : '')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setQuickClientOpen((prev) => !prev)}
                      data-testid="service-quick-create-client-toggle"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Criar Cliente
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="text-amber-700 border-amber-300"
                      onClick={handleUseDefaultClient}
                      data-testid="service-use-default-client-button"
                    >
                      Autorizar Cliente Indiferenciado
                    </Button>
                  </div>

                  {quickClientOpen && (
                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-lg border bg-white" data-testid="service-quick-create-client-panel">
                      <div className="space-y-2">
                        <Label>Nome *</Label>
                        <Input
                          value={quickClientForm.name}
                          onChange={(e) => setQuickClientForm((prev) => ({ ...prev, name: e.target.value }))}
                          placeholder="Nome do cliente"
                          data-testid="quick-client-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Telefone *</Label>
                        <Input
                          value={quickClientForm.phone}
                          onChange={(e) => setQuickClientForm((prev) => ({ ...prev, phone: e.target.value }))}
                          placeholder="+258 84 XXX XXXX"
                          data-testid="quick-client-phone-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>NUIT (Opcional)</Label>
                        <Input
                          value={quickClientForm.nuit}
                          onChange={(e) => setQuickClientForm((prev) => ({ ...prev, nuit: e.target.value }))}
                          data-testid="quick-client-nuit-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Morada/Cidade (Opcional)</Label>
                        <Input
                          value={quickClientForm.address}
                          onChange={(e) => setQuickClientForm((prev) => ({ ...prev, address: e.target.value }))}
                          placeholder="Morada"
                          data-testid="quick-client-address-input"
                        />
                      </div>
                      <div className="md:col-span-2 flex justify-end">
                        <Button
                          type="button"
                          onClick={handleQuickCreateClient}
                          disabled={creatingQuickClient}
                          data-testid="quick-client-create-button"
                        >
                          {creatingQuickClient ? 'A criar...' : 'Criar Cliente e Selecionar'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="customer_name">Nome do Cliente</Label>
                    <Input id="customer_name" value={formData.customer_name} readOnly data-testid="service-selected-customer-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer_phone">Telefone</Label>
                    <Input id="customer_phone" value={formData.customer_phone} readOnly data-testid="service-selected-customer-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="customer_email">Email</Label>
                    <Input id="customer_email" type="email" value={formData.customer_email} readOnly data-testid="service-selected-customer-email" />
                  </div>
                </div>
              </TabsContent>
              
              {/* Device Tab */}
              <TabsContent value="device" className="space-y-4 mt-4">
                {/* Article Origin - 4 options now */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Origem do Equipamento *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div 
                      className={cn(
                        "border rounded-lg p-3 cursor-pointer transition-all",
                        formData.article_origin === 'client' 
                          ? "border-indigo-500 bg-indigo-50" 
                          : "hover:border-slate-300"
                      )}
                      onClick={() => {
                        setFormData({...formData, article_origin: 'client', service_case_type: 'paid_repair', skip_diagnostic_charge: false, diagnostic_type: 'simple'});
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-indigo-600" />
                        <span className="font-medium text-sm">Cliente - Reparação Paga</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Dispositivo do cliente para reparação normal
                      </p>
                    </div>
                    <div 
                      className={cn(
                        "border rounded-lg p-3 cursor-pointer transition-all",
                        formData.article_origin === 'warranty_claim' 
                          ? "border-green-500 bg-green-50" 
                          : "hover:border-slate-300"
                      )}
                      onClick={() => {
                        setFormData({...formData, article_origin: 'warranty_claim', service_case_type: 'warranty_claim', skip_diagnostic_charge: true, diagnostic_type: 'no_charge'});
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-green-600" />
                        <span className="font-medium text-sm">Reclamação de Garantia</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Item vendido com garantia activa
                      </p>
                    </div>
                    <div 
                      className={cn(
                        "border rounded-lg p-3 cursor-pointer transition-all",
                        formData.article_origin === 'store' 
                          ? "border-amber-500 bg-amber-50" 
                          : "hover:border-slate-300"
                      )}
                      onClick={() => handleArticleOriginChange('store')}
                    >
                      <div className="flex items-center gap-2">
                        <Store className="h-4 w-4 text-amber-600" />
                        <span className="font-medium text-sm">Stock para Reparação</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Item do stock CG Business para reparar e vender
                      </p>
                    </div>
                    <div 
                      className={cn(
                        "border rounded-lg p-3 cursor-pointer transition-all",
                        formData.article_origin === 'store_equipment' 
                          ? "border-purple-500 bg-purple-50" 
                          : "hover:border-slate-300"
                      )}
                      onClick={() => {
                        setFormData({...formData, article_origin: 'store_equipment', service_case_type: 'store_internal', skip_diagnostic_charge: true, diagnostic_type: 'no_charge'});
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Settings className="h-4 w-4 text-purple-600" />
                        <span className="font-medium text-sm">Equipamento Interno</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Equipamento da loja (sem diagnóstico)
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* P0-CORE: Equipamento Interno - Selector de Ativos */}
                {formData.article_origin === 'store_equipment' && (
                  <div className="border rounded-lg p-4 bg-purple-50 space-y-3">
                    <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 flex items-start gap-2 mb-3">
                      <Lock className="h-4 w-4 text-purple-600 mt-0.5" />
                      <div className="text-sm text-purple-800">
                        <strong>Equipamento Interno:</strong> O diagnóstico será automaticamente bloqueado e sem custos para equipamentos da loja.
                      </div>
                    </div>
                    
                    <InternalAssetSelector
                      locationId={filters.location_id}
                      onAssetSelected={(assetData) => {
                        setFormData(prev => ({
                          ...prev,
                          location_id: prev.location_id || assetData.location_id || filters.location_id || null,
                          internal_asset_id: assetData.internal_asset_id,
                          device_brand: assetData.asset_brand || prev.device_brand,
                          device_model: assetData.asset_model || prev.device_model,
                          device_serial: assetData.serial_number || prev.device_serial,
                          device_specs: assetData.specs || prev.device_specs,
                          device_category: assetData.category?.toLowerCase() || prev.device_category
                        }));
                        toast.success('Dados do equipamento preenchidos automaticamente');
                      }}
                      selectedAssetId={formData.internal_asset_id}
                    />
                  </div>
                )}
                
                {/* P0-CORE: Warranty Claim - Busca de Venda */}
                {formData.article_origin === 'warranty_claim' && (
                  <div className="border rounded-lg p-4 bg-green-50 space-y-3">
                    <div className="flex items-center gap-2 text-green-800 font-medium">
                      <Receipt className="h-4 w-4" />
                      Reclamação de Garantia - Ligar a Venda
                    </div>
                    
                    {/* P0-CORE: Warranty Sale Search Component */}
                    <WarrantySaleSearch
                      locationId={filters.location_id}
                      onSaleSelected={(saleData) => {
                        setFormData(prev => ({
                          ...prev,
                          location_id: prev.location_id || saleData.location_id || filters.location_id || null,
                          sale_id: saleData.sale_id,
                          sale_item_id: saleData.sale_item_id,
                          sale_item_index: saleData.sale_item_index ?? 0,
                          original_invoice_no: saleData.sale_number,
                          purchase_date: saleData.sale_date?.slice(0, 10),
                          warranty_months: saleData.warranty_months?.toString() || '12',
                          client_id: saleData.client_id || prev.client_id,
                          use_default_client: false,
                          customer_name: saleData.customer_name || prev.customer_name,
                          customer_phone: saleData.customer_phone || prev.customer_phone,
                          device_category: saleData.product_category?.toLowerCase() || prev.device_category,
                          device_brand: saleData.product_brand || prev.device_brand,
                          device_model: saleData.product_model || prev.device_model,
                          device_serial: saleData.serial_number || prev.device_serial,
                          device_specs: saleData.specs || prev.device_specs,
                          skip_diagnostic_charge: true,
                          diagnostic_type: 'no_charge'
                        }));
                        toast.success('Dados da venda preenchidos automaticamente');
                      }}
                      selectedSaleId={formData.sale_id}
                    />
                    
                    {/* Warranty period override if needed */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                      <div className="space-y-1">
                        <Label className="text-xs">Período de Garantia (meses)</Label>
                        <Select 
                          value={formData.warranty_months} 
                          onValueChange={(v) => setFormData({...formData, warranty_months: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="3">3 meses</SelectItem>
                            <SelectItem value="6">6 meses</SelectItem>
                            <SelectItem value="12">12 meses</SelectItem>
                            <SelectItem value="24">24 meses</SelectItem>
                            <SelectItem value="36">36 meses</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Nº Documento Original</Label>
                        <Input
                          value={formData.original_invoice_no}
                          onChange={(e) => setFormData({...formData, original_invoice_no: e.target.value})}
                          placeholder="INV-2025-..."
                          disabled={!!formData.sale_id}
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Categoria *</Label>
                    <Select 
                      value={formData.device_category} 
                      onValueChange={handleCategoryChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {productCategories.map(cat => {
                          const IconComp = CATEGORY_ICONS[cat.base_prefix?.toUpperCase()] || Package;
                          return (
                            <SelectItem key={cat.id} value={cat.base_prefix?.toLowerCase() || cat.name.toLowerCase()}>
                              <div className="flex items-center gap-2">
                                <IconComp className="h-4 w-4" />
                                {cat.name}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="device_brand">Marca</Label>
                    <Input
                      id="device_brand"
                      value={formData.device_brand}
                      onChange={(e) => setFormData({...formData, device_brand: e.target.value})}
                      placeholder="Samsung, Apple, HP"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="device_model">Modelo</Label>
                    <Input
                      id="device_model"
                      value={formData.device_model}
                      onChange={(e) => setFormData({...formData, device_model: e.target.value})}
                      placeholder="Galaxy A06, iPhone 13"
                    />
                  </div>
                </div>
                
                {/* P0-CORE: Stock para Reparação - Novo Componente */}
                {formData.article_origin === 'store' && (
                  <div className="border rounded-lg p-4 bg-amber-50">
                    <RepairStockSelector
                      locationId={null}
                      preferredLocationId={filters.location_id}
                      onItemSelected={(itemData) => {
                        setFormData(prev => ({
                          ...prev,
                          location_id: prev.location_id || itemData.location_id || filters.location_id || null,
                          store_item_id: itemData.inventory_item_id,
                          equipment_source_type: 'repair_stock',
                          equipment_source_id: itemData.inventory_item_id,
                          device_brand: itemData.product_brand || prev.device_brand,
                          device_model: itemData.product_model || prev.device_model,
                          device_serial: itemData.serial_number || prev.device_serial,
                          device_specs: itemData.specs_final || itemData.specs || prev.device_specs,
                          device_category: itemData.category_name?.toLowerCase() || prev.device_category
                        }));
                        toast.success('Dados do item preenchidos automaticamente');
                      }}
                      selectedItemId={formData.store_item_id}
                    />
                  </div>
                )}
                
                {/* Device Serial (for client devices) */}
                {formData.article_origin === 'client' && (
                  <div className="space-y-2">
                    <Label htmlFor="device_serial">IMEI / Número de Série (se conhecido)</Label>
                    <Input
                      id="device_serial"
                      value={formData.device_serial}
                      onChange={(e) => setFormData({...formData, device_serial: e.target.value})}
                      placeholder="359XXXXXXXXXXXXXXX"
                    />
                  </div>
                )}
                
                {/* Dynamic Specs */}
                {currentSchema && (
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <h3 className="font-medium mb-3 flex items-center gap-2">
                      <Settings className="h-4 w-4 text-indigo-600" />
                      Especificações do Equipamento
                    </h3>
                    <DynamicSpecsForm
                      schema={currentSchema}
                      specs={formData.device_specs}
                      onChange={(newSpecs) => setFormData({...formData, device_specs: newSpecs})}
                    />
                  </div>
                )}
              </TabsContent>
              
              {/* Issue Tab */}
              <TabsContent value="issue" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="issue_description">Descrição do Problema *</Label>
                  <Textarea
                    id="issue_description"
                    value={formData.issue_description}
                    onChange={(e) => setFormData({...formData, issue_description: e.target.value})}
                    required
                    rows={5}
                    placeholder="Descreva detalhadamente o problema reportado pelo cliente..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notas Adicionais</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows={3}
                    placeholder="Observações internas..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    Data/Hora do Registo
                    <span className="text-xs text-muted-foreground">(opcional)</span>
                  </Label>
                  <Input
                    type="datetime-local"
                    value={formData.registered_at}
                    onChange={(e) => setFormData({...formData, registered_at: e.target.value})}
                    max={new Date().toISOString().slice(0, 16)}
                    className="border-amber-200 focus:border-amber-400"
                  />
                  <p className="text-xs text-muted-foreground">
                    Use este campo se o serviço foi recebido numa data/hora diferente
                  </p>
                </div>
              </TabsContent>
              
              {/* Diagnostic Tab */}
              <TabsContent value="diagnostic" className="space-y-4 mt-4">
                {/* BLOCKED FOR INTERNAL/WARRANTY */}
                {(formData.article_origin === 'store_equipment' || formData.service_case_type === 'store_internal' || formData.article_origin === 'warranty_claim' || formData.service_case_type === 'warranty_claim') ? (
                  <div className="border-2 border-dashed border-purple-300 rounded-lg p-8 bg-purple-50 text-center">
                    <Lock className="h-12 w-12 text-purple-400 mx-auto mb-3" />
                    <h3 className="font-bold text-purple-800 text-lg">Diagnóstico Bloqueado</h3>
                    <p className="text-purple-600 mt-2">
                      {formData.article_origin === 'warranty_claim' || formData.service_case_type === 'warranty_claim'
                        ? 'Reclamação de garantia não cobra diagnóstico simples/complexo.'
                        : 'Equipamento interno da loja não tem cobrança de diagnóstico.'}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-purple-200">
                      <span className="text-sm text-purple-700">Custo de Diagnóstico:</span>
                      <span className="font-bold text-green-600">0 MT (Gratuito)</span>
                    </div>
                  </div>
                ) : (
                  <>
                <div className="grid grid-cols-3 gap-4">
                  <div 
                    className={cn(
                      "border rounded-lg p-4 cursor-pointer transition-all",
                      formData.diagnostic_type === 'simple' 
                        ? "border-green-500 bg-green-50" 
                        : "hover:border-slate-300"
                    )}
                    onClick={() => setFormData({...formData, diagnostic_type: 'simple', skip_diagnostic_charge: false})}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Diagnóstico Simples</span>
                      <span className="text-lg font-bold text-green-600">
                        {formatCurrency(DIAGNOSTIC_COSTS.simple)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Verificação básica de software e hardware
                    </p>
                  </div>
                  <div 
                    className={cn(
                      "border rounded-lg p-4 cursor-pointer transition-all",
                      formData.diagnostic_type === 'complex' 
                        ? "border-purple-500 bg-purple-50" 
                        : "hover:border-slate-300"
                    )}
                    onClick={() => setFormData({...formData, diagnostic_type: 'complex', skip_diagnostic_charge: false})}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Diagnóstico Complexo</span>
                      <span className="text-lg font-bold text-purple-600">
                        {formatCurrency(DIAGNOSTIC_COSTS.complex)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Análise profunda, desmontagem, testes avançados
                    </p>
                  </div>
                  <div 
                    className={cn(
                      "border rounded-lg p-4 cursor-pointer transition-all",
                      formData.diagnostic_type === 'no_charge' 
                        ? "border-slate-500 bg-slate-50" 
                        : "hover:border-slate-300"
                    )}
                    onClick={() => setFormData({...formData, diagnostic_type: 'no_charge', skip_diagnostic_charge: true})}
                    data-testid="service-diagnostic-no-charge-option"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Diagnóstico Não Pago</span>
                      <span className="text-lg font-bold text-green-600">
                        {formatCurrency(DIAGNOSTIC_COSTS.no_charge)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Isento (0 MT) para casos validados.
                    </p>
                  </div>
                </div>
                  </>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="estimated_hours">Tempo Estimado (horas)</Label>
                  <Input
                    id="estimated_hours"
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={formData.estimated_hours}
                    onChange={(e) => setFormData({...formData, estimated_hours: e.target.value})}
                    placeholder="2"
                  />
                  <p className="text-xs text-muted-foreground">
                    Tempo previsto para diagnóstico e reparação
                  </p>
                </div>
              </TabsContent>
            </Tabs>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="create-service-submit-button">
                Criar Serviço
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reception Checklist Dialog */}
      <Dialog open={receptionDialogOpen} onOpenChange={setReceptionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-cyan-600" />
              Checklist de Recepção - CG Business
            </DialogTitle>
          </DialogHeader>
          
          {selectedService && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p><strong>Serviço:</strong> {formatDisplayOnlyCode(selectedService.service_number, 'SRV')}</p>
                <p><strong>Cliente:</strong> {selectedService.customer_name}</p>
                <p><strong>Equipamento:</strong> {selectedService.device_brand} {selectedService.device_model}</p>
              </div>
              
              <div className="space-y-3">
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={receptionChecklist.physical_condition_verified}
                    onChange={(e) => setReceptionChecklist({...receptionChecklist, physical_condition_verified: e.target.checked})}
                    className="h-5 w-5"
                  />
                  <div>
                    <p className="font-medium">Estado Físico Verificado</p>
                    <p className="text-xs text-muted-foreground">Riscos, danos, estado geral confirmado</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={receptionChecklist.accessories_registered}
                    onChange={(e) => setReceptionChecklist({...receptionChecklist, accessories_registered: e.target.checked})}
                    className="h-5 w-5"
                  />
                  <div>
                    <p className="font-medium">Acessórios Registados</p>
                    <p className="text-xs text-muted-foreground">Carregador, capa, etc.</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={receptionChecklist.customer_complaint_recorded}
                    onChange={(e) => setReceptionChecklist({...receptionChecklist, customer_complaint_recorded: e.target.checked})}
                    className="h-5 w-5"
                  />
                  <div>
                    <p className="font-medium">Relato do Cliente Registado</p>
                    <p className="text-xs text-muted-foreground">Problema descrito em detalhe</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={receptionChecklist.diagnosis_authorization}
                    onChange={(e) => setReceptionChecklist({...receptionChecklist, diagnosis_authorization: e.target.checked})}
                    className="h-5 w-5"
                  />
                  <div>
                    <p className="font-medium">Autorização de Diagnóstico</p>
                    <p className="text-xs text-muted-foreground">Cliente autorizou diagnóstico pago</p>
                  </div>
                </label>
                
                <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50">
                  <input
                    type="checkbox"
                    checked={receptionChecklist.customer_data_confirmed}
                    onChange={(e) => setReceptionChecklist({...receptionChecklist, customer_data_confirmed: e.target.checked})}
                    className="h-5 w-5"
                  />
                  <div>
                    <p className="font-medium">Dados do Cliente Confirmados</p>
                    <p className="text-xs text-muted-foreground">Nome, telefone, email verificados</p>
                  </div>
                </label>
              </div>
              
              <div className="space-y-2">
                <Label>Notas sobre Estado Físico</Label>
                <Textarea
                  value={receptionChecklist.physical_condition_notes}
                  onChange={(e) => setReceptionChecklist({...receptionChecklist, physical_condition_notes: e.target.value})}
                  placeholder="Ex: Ecrã com risco no canto inferior direito..."
                  rows={2}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Acessórios Entregues (separados por vírgula)</Label>
                <Input
                  value={receptionChecklist.accessories_list}
                  onChange={(e) => setReceptionChecklist({...receptionChecklist, accessories_list: e.target.value})}
                  placeholder="Carregador, Capa, Auriculares"
                />
              </div>
              
              {/* Warning for editing existing checklist */}
              {selectedService?.reception_checklist && (
                <div className="space-y-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">Edição de Checklist</p>
                      <p className="text-amber-700 text-xs">
                        Esta alteração ficará registada no histórico de auditoria.
                      </p>
                    </div>
                  </div>
                  
                  {/* Mandatory correction reason */}
                  <div className="space-y-2">
                    <Label className="text-amber-700 font-medium text-sm">
                      Motivo da Correcção *
                    </Label>
                    <Textarea
                      value={checklistCorrectionReason}
                      onChange={(e) => setChecklistCorrectionReason(e.target.value)}
                      placeholder="Descreva o motivo desta correcção (obrigatório)..."
                      rows={2}
                      className="border-amber-300 focus:border-amber-500"
                    />
                    <p className="text-xs text-amber-600">
                      Mínimo 5 caracteres. Obrigatório para editar checklist existente.
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setReceptionDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCompleteReception}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {selectedService?.reception_checklist ? 'Actualizar Checklist' : 'Concluir Recepção'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Service Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="w-[min(96vw,980px)] max-w-[980px] max-h-[88vh] overflow-y-auto p-4 sm:p-5">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-amber-600" />
              Editar Serviço - {formatDisplayOnlyCode(selectedService?.service_number, 'SRV')}
            </DialogTitle>
          </DialogHeader>
          
          {editFormData && (
            <div className="space-y-4">
              {/* Warning banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Edição Controlada</p>
                    <p className="text-sm text-amber-700">
                      Todas as alterações ficam registadas no histórico de auditoria com justificação obrigatória.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Locked fields warning if service started */}
              {editFormData._serviceStarted && (
                <div className="bg-slate-100 border border-slate-300 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Lock className="h-5 w-5 text-slate-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-800">Serviço em progresso</p>
                      <p className="text-sm text-slate-600">
                        IMEI/SN e categoria estão bloqueados após início do serviço.
                        {!isAdmin && ' Apenas Admin pode alterar.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Device Data */}
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <Laptop className="h-4 w-4" /> Equipamento
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className={editFormData._serviceStarted && !isAdmin ? 'text-muted-foreground' : ''}>
                      Categoria {editFormData._serviceStarted && !isAdmin && <Lock className="h-3 w-3 inline ml-1" />}
                    </Label>
                    <Select 
                      value={editFormData.device_category} 
                      onValueChange={(v) => setEditFormData({...editFormData, device_category: v})}
                      disabled={editFormData._serviceStarted && !isAdmin}
                    >
                      <SelectTrigger className={editFormData._serviceStarted && !isAdmin ? 'bg-slate-100' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {productCategories.map(cat => (
                          <SelectItem key={cat.id} value={cat.base_prefix?.toLowerCase() || cat.name.toLowerCase()}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Marca</Label>
                    <Input
                      value={editFormData.device_brand}
                      onChange={(e) => setEditFormData({...editFormData, device_brand: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Modelo</Label>
                    <Input
                      value={editFormData.device_model}
                      onChange={(e) => setEditFormData({...editFormData, device_model: e.target.value})}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className={editFormData._serviceStarted && !isAdmin ? 'text-muted-foreground' : ''}>
                    IMEI / Número de Série {editFormData._serviceStarted && !isAdmin && <Lock className="h-3 w-3 inline ml-1" />}
                  </Label>
                  <Input
                    value={editFormData.device_serial}
                    onChange={(e) => setEditFormData({...editFormData, device_serial: e.target.value})}
                    disabled={editFormData._serviceStarted && !isAdmin}
                    className={editFormData._serviceStarted && !isAdmin ? 'bg-slate-100' : ''}
                  />
                </div>
              </div>
              
              {/* Diagnostic & Time */}
              <div className="space-y-3">
                <h3 className="font-medium flex items-center gap-2">
                  <Settings className="h-4 w-4" /> Diagnóstico e Tempo
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={!canEditDiagnosticCost ? 'text-muted-foreground' : ''}>
                      Tipo de Diagnóstico {!canEditDiagnosticCost && <Lock className="h-3 w-3 inline ml-1" />}
                    </Label>
                    <Select 
                      value={editFormData.diagnostic_type} 
                      onValueChange={(v) => setEditFormData({...editFormData, diagnostic_type: v})}
                      disabled={!canEditDiagnosticCost}
                    >
                      <SelectTrigger className={!canEditDiagnosticCost ? 'bg-slate-100' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="simple">Simples - {formatCurrency(DIAGNOSTIC_COSTS.simple)}</SelectItem>
                        <SelectItem value="complex">Complexo - {formatCurrency(DIAGNOSTIC_COSTS.complex)}</SelectItem>
                        <SelectItem value="no_charge">Não Pago - {formatCurrency(DIAGNOSTIC_COSTS.no_charge)}</SelectItem>
                      </SelectContent>
                    </Select>
                    {!canEditDiagnosticCost && (
                      <p className="text-xs text-muted-foreground">Apenas Admin pode alterar</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Tempo Estimado (horas)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={editFormData.estimated_hours}
                      onChange={(e) => setEditFormData({...editFormData, estimated_hours: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              
              {/* Notes */}
              <div className="space-y-2">
                <Label>Notas Técnicas</Label>
                <Textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                  rows={3}
                />
              </div>
              
              {/* MANDATORY Justification */}
              <div className="space-y-2 border-t pt-4">
                <Label className="text-amber-700 font-medium">
                  Justificação da Alteração *
                </Label>
                <Textarea
                  value={editJustification}
                  onChange={(e) => setEditJustification(e.target.value)}
                  placeholder="Descreva o motivo desta alteração (obrigatório)..."
                  rows={2}
                  className="border-amber-300 focus:border-amber-500"
                />
                <p className="text-xs text-muted-foreground">
                  Mínimo 5 caracteres. Esta justificação será registada no histórico de auditoria.
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveEdit}
                  disabled={!editJustification || editJustification.trim().length < 5}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Guardar Alterações
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Confirmation Dialog */}
      <AlertDialog open={confirmEditDialogOpen} onOpenChange={setConfirmEditDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Confirmar Alteração
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>Esta alteração ficará registada no histórico de auditoria com:</p>
              <ul className="list-disc list-inside text-sm space-y-1 text-slate-700">
                <li><strong>Utilizador:</strong> {user?.full_name}</li>
                <li><strong>Data/Hora:</strong> {new Date().toLocaleString('pt-MZ', { timeZone: 'Africa/Maputo' })}</li>
                <li><strong>Justificação:</strong> &ldquo;{editJustification}&rdquo;</li>
              </ul>
              <p className="font-medium pt-2">Deseja continuar?</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmEdit}>
              Confirmar e Guardar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
        <DialogContent className="w-[min(96vw,1080px)] max-w-[1080px] max-h-[88vh] overflow-y-auto p-4 sm:p-5">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-indigo-600" />
              Histórico de Alterações - {formatDisplayOnlyCode(selectedService?.service_number, 'SRV')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-4 p-3 bg-slate-50 rounded-lg">
              <div className="flex-1">
                <Label className="text-xs">Filtrar por Utilizador</Label>
                <Input
                  placeholder="Nome do utilizador..."
                  value={auditFilter.user}
                  onChange={(e) => setAuditFilter({...auditFilter, user: e.target.value})}
                  className="h-8 mt-1"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs">Filtrar por Data</Label>
                <Input
                  type="date"
                  value={auditFilter.date}
                  onChange={(e) => setAuditFilter({...auditFilter, date: e.target.value})}
                  className="h-8 mt-1"
                />
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="self-end"
                onClick={() => setAuditFilter({ user: '', date: '' })}
              >
                Limpar
              </Button>
            </div>
            
            {auditLogs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                Sem alterações registadas
              </p>
            ) : (
              <div className="space-y-3">
                {auditLogs
                  .filter(log => {
                    // Filter by user
                    if (auditFilter.user && !(log.user_name || '').toLowerCase().includes(auditFilter.user.toLowerCase())) {
                      return false;
                    }
                    // Filter by date
                    if (auditFilter.date) {
                      const parsedLogDate = log.timestamp ? new Date(log.timestamp) : null;
                      if (!parsedLogDate || Number.isNaN(parsedLogDate.getTime())) return false;
                      const logDate = parsedLogDate.toISOString().split('T')[0];
                      if (logDate !== auditFilter.date) return false;
                    }
                    return true;
                  })
                  .map((log) => (
                  <div key={log.id} className="border rounded-lg p-4 bg-white shadow-sm" data-testid={`service-history-row-${log.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={cn(
                          log.action === 'edit' && 'bg-amber-50 text-amber-700 border-amber-300',
                          log.action === 'reception_edit' && 'bg-cyan-50 text-cyan-700 border-cyan-300',
                          log.action === 'battery_correction' && 'bg-purple-50 text-purple-700 border-purple-300',
                          log.action === 'admin_note' && 'bg-purple-50 text-purple-700 border-purple-300'
                        )}>
                          {log.action === 'edit' && 'Edição'}
                          {log.action === 'reception_edit' && 'Checklist Editada'}
                          {log.action === 'battery_correction' && 'Correcção Bateria'}
                          {log.action === 'admin_note' && 'Nota Admin'}
                        </Badge>
                        {log.user_role && (
                          <Badge variant="secondary" className="text-xs">
                            {log.user_role === 'admin' ? 'Admin' : 
                             log.user_role === 'store_manager' ? 'Gerente' : log.user_role}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatDateTime(log.timestamp)}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-3">
                      <User className="h-4 w-4 text-slate-500" />
                      <span className="text-sm font-medium">{log.user_name}</span>
                    </div>
                    
                    {/* Changes table */}
                    <div className="bg-slate-50 rounded-lg p-3 mb-3">
                      <p className="text-xs font-medium text-slate-600 mb-2">Alterações:</p>
                      <div className="space-y-2">
                        {normalizeAuditChanges(log.changes).map((change, i) => (
                          <div key={getAuditChangeKey(change, i)} className="flex items-center gap-2 text-sm">
                            <span className="font-medium text-slate-700 min-w-[120px]">
                              {FIELD_LABELS[change.field] || change.field}:
                            </span>
                            <span className="text-red-600 bg-red-50 px-2 py-0.5 rounded line-through">
                              {String(change.old_value ?? '-')}
                            </span>
                            <span className="text-slate-400">→</span>
                            <span className="text-green-600 bg-green-50 px-2 py-0.5 rounded">
                              {String(change.new_value ?? '-')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Justification */}
                    {(log.justification || log.correction_reason) && (
                      <div className="border-t pt-3">
                        <p className="text-xs font-medium text-amber-700 mb-1">
                          {log.action === 'reception_edit' ? 'Motivo da Correcção:' : 'Justificação:'}
                        </p>
                        <p className="text-sm text-slate-700 bg-amber-50 p-2 rounded border border-amber-200">
                          &ldquo;{log.justification || log.correction_reason}&rdquo;
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Services Table */}
      <Card className="border-slate-200 shadow-sm" data-testid="services-table">
        <CardHeader className="flex flex-col gap-3 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Wrench className="h-4 w-4" />
            Pedidos de Serviço ({services.length})
          </CardTitle>
          <div className="flex flex-wrap gap-2 justify-end">
            {selectedServiceIds.length > 0 && (
              <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-slate-700" data-testid="services-selection-badge">
                {selectedServiceIds.length} seleccionad{selectedServiceIds.length > 1 ? 'os' : 'o'}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-full px-3 text-[11px]"
              disabled={!selectedServiceIds.length}
              onClick={() => handleBulkServiceStatus('deactivate')}
              data-testid="services-bulk-deactivate-button"
            >
              Desativar ({selectedServiceIds.length})
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-full px-3 text-[11px]"
              disabled={!selectedServiceIds.length}
              onClick={() => handleBulkServiceStatus('restore_soft')}
              data-testid="services-bulk-restore-button"
            >
              Restaurar
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-full px-3 text-[11px]"
              disabled={!selectedServiceIds.length}
              onClick={handleExportSelectedServices}
              data-testid="services-bulk-export-button"
            >
              Exportar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-3 pt-0">
          {loading ? (
            <p className="text-center text-muted-foreground py-4">A carregar serviços...</p>
          ) : services.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">Sem pedidos de serviço</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] leading-tight" data-testid="services-main-table">
                <thead className="border-b border-slate-200 bg-slate-50/95">
                  <tr>
                    <th className={INVENTORY_TABLE_HEAD_CLASS}>
                      <Checkbox
                        checked={allServicesSelected}
                        onCheckedChange={(checked) => toggleSelectAllServices(Boolean(checked))}
                        data-testid="services-select-all-checkbox"
                      />
                    </th>
                    <th className={INVENTORY_TABLE_HEAD_CLASS}>Nº Serviço</th>
                    <th className={INVENTORY_TABLE_HEAD_CLASS}>Data/Hora</th>
                    <th className={INVENTORY_TABLE_HEAD_CLASS}>Cliente</th>
                    <th className={INVENTORY_TABLE_HEAD_CLASS}>Equipamento</th>
                    <th className={INVENTORY_TABLE_HEAD_CLASS}>Origem</th>
                    <th className={INVENTORY_TABLE_HEAD_CLASS}>Tempo</th>
                    <th className={INVENTORY_TABLE_HEAD_CLASS}>Estado</th>
                    <th className={`${INVENTORY_TABLE_HEAD_CLASS} text-right`}>Custo</th>
                    <th className={`${INVENTORY_TABLE_HEAD_CLASS} text-center`}>Acções</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((service) => (
                    <tr key={service.id} className={cn(INVENTORY_TABLE_ROW_CLASS, selectedServiceIds.includes(service.id) && 'bg-indigo-50/35')} data-testid={`service-row-${service.id}`}>
                      <td className={INVENTORY_TABLE_CELL_CLASS}>
                        <Checkbox
                          checked={selectedServiceIds.includes(service.id)}
                          onCheckedChange={(checked) => toggleServiceSelection(service.id, Boolean(checked))}
                          data-testid={`service-select-checkbox-${service.id}`}
                        />
                      </td>
                      <td className={INVENTORY_TABLE_CELL_CLASS}>
                        <div className="space-y-0.5">
                          <p className="font-mono text-[11px] font-semibold text-slate-900">{formatDisplayOnlyCode(service.service_number, service.service_type === 'warranty_claim' ? 'WAR' : 'SRV')}</p>
                          <p className="text-[10px] text-slate-500">{service.assigned_technician_name || service.employee_name || 'Sem técnico'}</p>
                        </div>
                      </td>
                      <td className={`${INVENTORY_TABLE_CELL_CLASS} text-[10px] text-muted-foreground`} data-testid={`service-created-at-${service.id}`}>
                        {formatDateTime(service.registered_at || service.created_at)}
                      </td>
                      <td className={INVENTORY_TABLE_CELL_CLASS}>
                        <div>
                          <p className="font-medium text-[11px]">{service.customer_name}</p>
                          <p className="text-[10px] text-muted-foreground">{service.customer_phone}</p>
                        </div>
                      </td>
                      <td className={INVENTORY_TABLE_CELL_CLASS}>
                        <div className="flex items-center gap-1">
                          {service.device_category === 'laptop' && <Laptop className="h-3.5 w-3.5 text-slate-500" />}
                          {service.device_category === 'smartphone' && <Smartphone className="h-3.5 w-3.5 text-slate-500" />}
                          {service.device_brand} {service.device_model || service.device_category}
                        </div>
                      </td>
                      <td className={INVENTORY_TABLE_CELL_CLASS}>
                        <div className="flex flex-wrap gap-1">
                          <Badge variant="outline" className="text-[10px]">
                            {service.article_origin === 'store' && (
                              <><Store className="h-3 w-3 mr-1" /> Loja</>
                            )}
                            {service.article_origin === 'store_equipment' && (
                              <><Settings className="h-3 w-3 mr-1" /> Interno</>
                            )}
                            {service.article_origin === 'warranty_claim' && (
                              <><Receipt className="h-3 w-3 mr-1" /> Garantia</>
                            )}
                            {service.article_origin === 'client' && (
                              <><User className="h-3 w-3 mr-1" /> Cliente</>
                            )}
                          </Badge>
                          {/* Warranty status badge */}
                          {service.is_warranty_claim && service.warranty_status && (
                            <Badge 
                              variant="outline" 
                              className={cn("text-[10px]", 
                                service.warranty_status === 'in_warranty' && "bg-green-50 text-green-700 border-green-300",
                                service.warranty_status === 'out_of_warranty' && "bg-red-50 text-red-700 border-red-300",
                                service.warranty_status === 'void' && "bg-orange-50 text-orange-700 border-orange-300"
                              )}
                            >
                              {service.warranty_status === 'in_warranty' && '✓ Em Garantia'}
                              {service.warranty_status === 'out_of_warranty' && '✗ Fora Garantia'}
                              {service.warranty_status === 'void' && '⚠ Garantia Anulada'}
                              {service.warranty_status === 'unknown' && '? Desconhecido'}
                            </Badge>
                          )}
                          {/* Diagnostic blocked indicator */}
                          {service.diagnostic_blocked && (
                            <Badge variant="outline" className="text-[10px] bg-purple-50 text-purple-700 border-purple-300">
                              <Lock className="h-3 w-3 mr-1" /> Diag. Bloq.
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className={INVENTORY_TABLE_CELL_CLASS}>
                        <div className="flex items-center gap-1">
                          {getTimeStatusIcon(service.time_tracking)}
                          <span className={cn("text-[11px]", timeStatusColors[service.time_tracking?.time_status || 'not_started'])}>
                            {service.time_tracking?.elapsed_hours 
                              ? `${service.time_tracking.elapsed_hours}h`
                              : '-'}
                          </span>
                        </div>
                      </td>
                      <td className={INVENTORY_TABLE_CELL_CLASS}>
                        <Badge className={statusColors[service.status]}>
                          {statusLabels[service.status] || service.status}
                        </Badge>
                      </td>
                      <td className={`${INVENTORY_TABLE_CELL_CLASS} text-right font-medium`}>
                        {formatCurrency(calculateServiceGrossTotal(service))}
                      </td>
                      <td className={INVENTORY_TABLE_CELL_CLASS}>
                        <UniversalCrudActions
                          entityLabel="service"
                          entityId={service.id}
                          canView
                          canEdit={canEditService(service)}
                          canDelete={canDelete}
                          canRecover={false}
                          align="center"
                          onView={() => handleViewDetails(service)}
                          onEdit={() => handleOpenEdit(service)}
                          onDelete={() => {
                            if (service.status === 'completed') {
                              setSelectedService(service);
                              setDeleteReason('');
                              setDeleteDialogOpen(true);
                              return;
                            }

                            if (isAdmin) {
                              handleDirectAdminDelete(service);
                              return;
                            }
                            setSelectedService(service);
                            setDeleteReason('');
                            setDeleteDialogOpen(true);
                          }}
                          extraActions={
                            <>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 rounded-md border border-slate-200 bg-slate-50 px-2 text-slate-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:bg-white"
                                    data-testid={`service-actions-menu-trigger-${service.id}`}
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-64 rounded-2xl border border-slate-200 bg-white p-1 shadow-xl" data-testid={`service-actions-menu-${service.id}`}>
                                  <DropdownMenuItem onClick={() => handleViewAuditLog(service)} data-testid={`history-service-${service.id}`}>
                                    <History className="h-4 w-4 text-indigo-600" />
                                    Ver histórico
                                  </DropdownMenuItem>

                                  {canAddAdminNote(service) && (
                                    <DropdownMenuItem onClick={() => handleOpenAdminNote(service)} data-testid={`admin-note-service-${service.id}`}>
                                      <Edit className="h-4 w-4 text-purple-600" />
                                      Nota administrativa
                                    </DropdownMenuItem>
                                  )}

                                  {service.status === 'pending' && (
                                    <DropdownMenuItem onClick={() => handleOpenReception(service)} data-testid={`start-reception-service-${service.id}`}>
                                      <ClipboardCheck className="h-4 w-4 text-cyan-600" />
                                      Iniciar recepção
                                    </DropdownMenuItem>
                                  )}

                                  {service.status === 'reception' && !service.time_tracking?.started_at && (
                                    <DropdownMenuItem onClick={() => handleStartService(service)} data-testid={`start-service-${service.id}`}>
                                      <Play className="h-4 w-4 text-green-600" />
                                      Iniciar serviço
                                    </DropdownMenuItem>
                                  )}

                                  {service.status === 'in_progress' && (
                                    <DropdownMenuItem onClick={() => handleCompleteService(service)} data-testid={`complete-service-${service.id}`}>
                                      <CheckCircle className="h-4 w-4 text-blue-600" />
                                      Concluir serviço
                                    </DropdownMenuItem>
                                  )}

                                  {canInvoice && ['ready_for_pickup', 'completed'].includes(service.status) && (
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setSelectedService(service);
                                        setInvoiceDialogOpen(true);
                                      }}
                                      data-testid={`invoice-service-${service.id}`}
                                    >
                                      <Receipt className="h-4 w-4 text-green-600" />
                                      Gerar factura
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>

                              <WhatsAppButton service={service} onMessageSent={loadServices} size="sm" variant="ghost" />
                            </>
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Admin Note Dialog */}
      <Dialog open={adminNoteDialogOpen} onOpenChange={setAdminNoteDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-purple-600" />
              Nota Administrativa - {formatDisplayOnlyCode(selectedService?.service_number, 'SRV')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Info banner */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-purple-600 mt-0.5" />
                <div>
                  <p className="font-medium text-purple-800">Serviço Fechado</p>
                  <p className="text-sm text-purple-700">
                    Este serviço está em estado final. Apenas notas administrativas podem ser adicionadas.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Service info */}
            {selectedService && (
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p><strong>Estado:</strong> {statusLabels[selectedService.status]}</p>
                <p><strong>Cliente:</strong> {selectedService.customer_name}</p>
                <p><strong>Equipamento:</strong> {selectedService.device_brand} {selectedService.device_model}</p>
              </div>
            )}
            
            {/* Note text */}
            <div className="space-y-2">
              <Label className="text-purple-700 font-medium">Nota Administrativa *</Label>
              <Textarea
                value={adminNoteText}
                onChange={(e) => setAdminNoteText(e.target.value)}
                placeholder="Escreva a nota administrativa..."
                rows={4}
                className="border-purple-300 focus:border-purple-500"
              />
            </div>
            
            {/* Justification */}
            <div className="space-y-2">
              <Label className="text-purple-700 font-medium">Justificação *</Label>
              <Textarea
                value={adminNoteJustification}
                onChange={(e) => setAdminNoteJustification(e.target.value)}
                placeholder="Motivo para adicionar esta nota..."
                rows={2}
                className="border-purple-300 focus:border-purple-500"
              />
              <p className="text-xs text-muted-foreground">
                Mínimo 5 caracteres. Será registado no histórico de auditoria.
              </p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setAdminNoteDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveAdminNote}
                className="bg-purple-600 hover:bg-purple-700"
                disabled={!adminNoteText || adminNoteText.trim().length < 5 || !adminNoteJustification || adminNoteJustification.trim().length < 5}
              >
                Adicionar Nota
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-green-600" />
              Gerar Factura - {formatDisplayOnlyCode(selectedService?.service_number, 'SRV')}
            </DialogTitle>
          </DialogHeader>
          
          {selectedService && (
            <div className="space-y-4">
              {/* Service Summary */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cliente:</span>
                  <span className="font-medium">{selectedService.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Equipamento:</span>
                  <span className="font-medium">
                    {selectedService.device_brand} {selectedService.device_model || selectedService.device_category}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Problema:</span>
                  <span className="font-medium text-sm truncate max-w-[200px]">
                    {selectedService.issue_description}
                  </span>
                </div>
              </div>
              
              {/* Cost Breakdown */}
              <div className="space-y-2 border-t pt-4">
                <h4 className="font-medium">Resumo de Custos</h4>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>
                      Diagnóstico ({selectedService.diagnostic_type === 'simple' ? 'Simples' : selectedService.diagnostic_type === 'complex' ? 'Complexo' : 'Não Pago'})
                    </span>
                    <span>{formatCurrency(selectedService.diagnostic_cost || DIAGNOSTIC_COSTS[selectedService.diagnostic_type] || 0)}</span>
                  </div>
                  {(selectedService.parts_used || []).length > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Peças ({selectedService.parts_used.length})</span>
                      <span>{formatCurrency(selectedService.parts_used.reduce((s, p) => s + (p.cost || 0), 0))}</span>
                    </div>
                  )}
                  {selectedService.labor_cost > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Mão de Obra</span>
                      <span>{formatCurrency(selectedService.labor_cost)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                    <span>Total</span>
                    <span className="text-green-600">
                      {formatCurrency(calculateServiceGrossTotal(selectedService))}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleGenerateInvoice} className="bg-green-600 hover:bg-green-700">
                  <FileText className="h-4 w-4 mr-2" />
                  Gerar Factura
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Eliminar Serviço
            </AlertDialogTitle>
            <AlertDialogDescription>
              Tem a certeza que deseja eliminar o serviço <strong>{formatDisplayOnlyCode(selectedService?.service_number, 'SRV')}</strong>?
              <br /><br />
              {selectedService?.status === 'completed' && (
                <>
                  <span className="font-medium text-red-600">
                    Serviço concluído: confirme cuidadosamente antes de eliminar.
                  </span>
                  <br /><br />
                </>
              )}
              <span className="text-amber-600">
                Esta acção irá marcar o serviço como eliminado. Apenas administradores podem reverter.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-2 py-4">
            <Label htmlFor="deleteReason">Motivo da Eliminação *</Label>
            <Textarea
              id="deleteReason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Descreva o motivo da eliminação (mínimo 5 caracteres)..."
              rows={3}
              data-testid="service-delete-reason-input"
            />
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteReason('');
            }}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteService}
              className="bg-red-600 hover:bg-red-700"
              disabled={!deleteReason || deleteReason.trim().length < 5}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Service Workflow Modal (Repair Flow) */}
      <ServiceDetailModal
        open={workflowModalOpen}
        onOpenChange={setWorkflowModalOpen}
        serviceId={workflowServiceId}
        onServiceUpdated={loadServices}
      />
      </div>
    </div>
  );
}
